import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Download, Mail, Send, CheckCircle, XCircle, FileText, Users, School, User, Activity } from 'lucide-react';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import Button from '../../components/ui/Button';

import TeamOverviewTab from './components/TeamOverviewTab';
import PlayerDetailsTab from './components/PlayerDetailsTab';
import DocumentsTab from './components/DocumentsTab';
import ActivityHistoryTab from './components/ActivityHistoryTab';
import SignedDeclarationTab from './components/SignedDeclarationTab';
import ApprovalModal from './components/ApprovalModal';
import RejectionModal from './components/RejectionModal';
import MessageModal from './components/MessageModal';
import Icon from '../../components/AppIcon';
import { registrationService } from '../../services/registrationService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';


const RegistrationDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);

  // Determine if this is user mode (from registration dashboard) or admin mode
  const isUserMode = location?.state?.userMode !== false; // Default to user mode unless explicitly set to false
  const userRole = isUserMode ? null : (localStorage.getItem('userRole') || 'reviewer'); // 'reviewer', 'super_admin', or 'comms_user'

  // Load registration data from Supabase
  // Define loadRegistrationData outside useEffect so it can be called from handlers
  const loadRegistrationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const registrationId = location?.state?.registrationId;
      
      if (!registrationId) {
        setError('Registration ID is missing');
        setLoading(false);
        return;
      }

      // Fetch registration details
      const registration = await registrationService.getRegistrationDetails(registrationId);
      
      // Fetch players
      const players = await registrationService.getPlayersByRegistration(registrationId);
      
      // Fetch coaches
      const coaches = await registrationService.getCoachesByRegistration(registrationId);
      
      // Load documents for all players in parallel
      const documentPromises = (players || []).map(async (player) => {
        try {
          const playerDocs = await registrationService.getPlayerDocuments(player.id);
          
          // Generate signed URLs for all documents in parallel
          const urlPromises = playerDocs.map(async (doc) => {
            try {
              const fileName = doc.file_path.replace('player-documents/', '');
              const { data: urlData, error: urlError } = await supabase?.storage
                ?.from('player-documents')
                ?.createSignedUrl(fileName, 3600); // 1 hour expiration
              
              if (urlError) {
                console.error(`Error creating signed URL for ${doc.file_path}:`, urlError);
              }
              
              // Use fileUrl as thumbnail for PDFs, or generate a placeholder
              const thumbnail = urlData?.signedUrl || '';
              
              return {
                id: doc.id,
                name: `${doc.document_type.replace('_', ' ')} - ${player.first_name} ${player.last_name}`,
                type: doc.document_type,
                playerId: player.id,
                playerName: `${player.first_name} ${player.middle_name ? player.middle_name + ' ' : ''}${player.last_name}`.trim(),
                uploadedDate: doc.uploaded_at,
                status: 'approved', // Default status
                fileUrl: urlData?.signedUrl || null,
                filePath: doc.file_path,
                fileSize: doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : 'N/A',
                thumbnail: thumbnail // Use fileUrl as thumbnail
              };
            } catch (error) {
              console.error(`Error processing document ${doc.id}:`, error);
              return null;
            }
          });
          
          // Wait for all signed URLs for this player's documents
          const processedDocs = await Promise.all(urlPromises);
          return processedDocs.filter(doc => doc !== null);
        } catch (error) {
          console.error(`Error loading documents for player ${player.id}:`, error);
          return [];
        }
      });
      
      // Wait for all players' documents to load in parallel
      const allDocumentsArrays = await Promise.all(documentPromises);
      const allDocuments = allDocumentsArrays.flat();

      // Transform data to match component expectations
      const primaryCoach = coaches?.find(c => c.is_primary) || coaches?.[0];
      const captain = players?.find(p => p.is_captain);
      
      const formatAgeCategory = (category) => {
        const map = {
          'boys_u14': 'Boys U14',
          'girls_u15': 'Girls U15',
          'boys_u16': 'Boys U16'
        };
        return map?.[category] || category;
      };

      const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return null;
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      };

      const transformedData = {
        id: registration.id,
        registrationNumber: registration.registration_number || registration.id,
        schoolName: registration?.schools?.name || registration?.school_name || 'Unknown School',
        schoolAddress: registration.schools?.address || '',
        schoolContact: registration.schools?.phone || '',
        schoolEmail: registration.school_email || registration.schools?.email || '',
        pocName: registration.poc_name || '',
        pocPhone: registration.mobile_number || '',
        pocEmail: registration.poc_email || '',
        category: formatAgeCategory(registration.age_category),
        city: registration.city || registration.schools?.city || '',
        status: registration.status || 'draft',
        submittedDate: registration.submitted_at || registration.created_at,
        lastUpdated: registration.updated_at || registration.created_at,
        completionPercentage: registrationService.calculateCompletionPercentage({
          ...registration,
          players: players || []
        }),
        coachName: primaryCoach?.name || '',
        coachPhone: '', // Coaches don't have phone in current schema
        coachEmail: '', // Coaches don't have email in current schema
        coachExperience: '', // Not in current schema
        totalPlayers: players?.length || 0,
        captainId: captain?.id || null,
        viceCaptainId: null, // Not in current schema
        declarationFilePath: registration.declaration_file_path || null,
        declarationUploaded: registration.declaration_uploaded || false,
        players: (players || []).map(player => ({
          id: player.id,
          name: `${player.first_name} ${player.middle_name ? player.middle_name + ' ' : ''}${player.last_name}`.trim(),
          dateOfBirth: player.date_of_birth,
          age: calculateAge(player.date_of_birth),
          role: player.is_captain ? 'Captain' : (player.role?.replace('_', ' ') || 'Player'),
          mcaId: player.mca_id || 'N/A',
          phone: player.mobile_number || '',
          email: player.email || '',
          address: '', // Not in current schema
          validationStatus: 'verified', // Default
          documentsComplete: allDocuments.filter(d => d.playerId === player.id).length >= 5
        })),
        documents: allDocuments,
        activities: [
          {
            id: 'ACT001',
            type: 'submission',
            description: `Registration ${registration.status === 'submitted' ? 'submitted' : 'created'} by ${registration.poc_name}`,
            timestamp: registration.submitted_at || registration.created_at,
            user: registration.poc_name,
            details: `Registration ${registration.status === 'submitted' ? 'submitted' : 'created'} with ${players?.length || 0} players`
          }
        ]
      };

      setRegistrationData(transformedData);
    } catch (err) {
      console.error('Error loading registration data:', err);
      setError(err?.message || 'Failed to load registration details');
    } finally {
      setLoading(false);
    }
  };

  // Call loadRegistrationData on mount and when registrationId changes
  useEffect(() => {
    loadRegistrationData();
  }, [location?.state?.registrationId]);

  const handleApprovalSuccess = () => {
    // Reload registration data after approval
    loadRegistrationData();
  };

  const handleRejectionSuccess = () => {
    // Reload registration data after rejection
    loadRegistrationData();
  };

  const tabs = [
    { id: 'overview', label: 'Team Overview', icon: School },
    { id: 'players', label: 'Player Details', icon: Users },
    { id: 'documents', label: 'Player Documents', icon: FileText },
    ...(isUserMode ? [] : [{ id: 'declaration', label: 'Signed Declaration', icon: FileText }]),
    { id: 'activity', label: 'Activity History', icon: Activity }
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading registration details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !registrationData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Error Loading Registration</p>
            <p className="text-sm">{error || 'Registration not found'}</p>
          </div>
          <Button onClick={() => navigate('/registration-dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }


  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      submitted: 'bg-blue-100 text-blue-800 border-blue-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pending_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      under_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      incomplete: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[status] || colors?.incomplete;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      submitted: 'Submitted',
      pending: 'Pending',
      pending_review: 'Pending Review',
      under_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected',
      incomplete: 'Incomplete'
    };
    return labels?.[status] || status || 'Unknown';
  };

  const handleApprove = () => {
    const currentStatus = registrationData?.status?.toLowerCase();
    
    // Check if registration is rejected - ask for confirmation
    if (currentStatus === 'rejected') {
      const confirm = window.confirm(
        'Warning: This registration has been rejected. Approving it will change the status to approved and this action cannot be easily undone.\n\nAre you absolutely sure you want to proceed?'
      );
      if (!confirm) {
        return;
      }
    }

    setShowApprovalModal(true);
  };

  const handleReject = () => {
    const currentStatus = registrationData?.status?.toLowerCase();
    
    // Check if registration is already rejected
    if (currentStatus === 'rejected') {
      alert('This registration has already been rejected. You cannot reject it again.');
      return;
    }

    // Check if registration is approved - ask for confirmation
    if (currentStatus === 'approved') {
      const confirm = window.confirm(
        'Warning: This registration is already approved. Rejecting it will change the status to rejected and this action cannot be easily undone.\n\nAre you absolutely sure you want to proceed?'
      );
      if (!confirm) {
        return;
      }
    }

    setShowRejectionModal(true);
  };

  const handleSendMessage = () => {
    setShowMessageModal(true);
  };

  const handleDownloadPDF = async () => {
    if (!registrationData) {
      alert('Registration data not available');
      return;
    }

    try {
      const jsPDF = (await import('jspdf')).default;
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      let y = 20;
      const pageWidth = 210;
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);

      // Title
      pdf.setFontSize(18);
      pdf.text('Registration Details', margin, y);
      y += 10;

      // Registration Number
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Registration Number:', margin, y);
      pdf.setFont(undefined, 'normal');
      pdf.text(registrationData.registrationNumber || 'N/A', margin + 60, y);
      y += 8;

      // School Information
      pdf.setFont(undefined, 'bold');
      pdf.text('School Information', margin, y);
      y += 6;
      pdf.setFont(undefined, 'normal');
      pdf.text(`School Name: ${registrationData.schoolName}`, margin, y);
      y += 6;
      pdf.text(`City: ${registrationData.city}`, margin, y);
      y += 6;
      pdf.text(`Category: ${registrationData.category}`, margin, y);
      y += 6;
      pdf.text(`Status: ${registrationData.status}`, margin, y);
      y += 10;

      // Coach Information
      pdf.setFont(undefined, 'bold');
      pdf.text('Coach Information', margin, y);
      y += 6;
      pdf.setFont(undefined, 'normal');
      pdf.text(`Coach Name: ${registrationData.coachName}`, margin, y);
      y += 6;
      pdf.text(`POC Name: ${registrationData.pocName}`, margin, y);
      y += 6;
      pdf.text(`POC Email: ${registrationData.pocEmail}`, margin, y);
      y += 6;
      pdf.text(`POC Phone: ${registrationData.pocPhone}`, margin, y);
      y += 10;

      // Players
      pdf.setFont(undefined, 'bold');
      pdf.text(`Players (${registrationData.totalPlayers})`, margin, y);
      y += 6;
      pdf.setFont(undefined, 'normal');
      
      if (registrationData.players && registrationData.players.length > 0) {
        registrationData.players.forEach((player, index) => {
          if (y > 270) { // New page if needed
            pdf.addPage();
            y = 20;
          }
          pdf.setFont(undefined, 'bold');
          pdf.text(`${index + 1}. ${player.name}`, margin, y);
          y += 5;
          pdf.setFont(undefined, 'normal');
          pdf.text(`   DOB: ${player.dateOfBirth ? new Date(player.dateOfBirth).toLocaleDateString('en-GB') : 'N/A'}`, margin, y);
          y += 5;
          pdf.text(`   Age: ${player.age || 'N/A'}`, margin, y);
          y += 5;
          pdf.text(`   Role: ${player.role}`, margin, y);
          y += 5;
          pdf.text(`   MCA ID: ${player.mcaId}`, margin, y);
          y += 8;
        });
      }

      // Footer
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(
          `Generated on ${new Date().toLocaleDateString('en-GB')} - Page ${i} of ${totalPages}`,
          margin,
          285
        );
      }

      // Download
      const filename = `Registration_${registrationData.registrationNumber || registrationData.id}_${new Date().getTime()}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const renderQuickActions = () => {
    const isDraft = registrationData?.status === 'draft';
    const isRejected = registrationData?.status === 'rejected';
    const isApproved = registrationData?.status === 'approved';

    if (userRole === 'reviewer') {
      return (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleApprove}
            disabled={isDraft || isApproved}
            className={`px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all ${
              isDraft || isApproved
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            <CheckCircle className="w-5 h-5" />
            Approve Registration
          </Button>
          <Button
            onClick={handleReject}
            disabled={isDraft || isRejected}
            className={`px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all ${
              isDraft || isRejected
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            <XCircle className="w-5 h-5" />
            Reject Registration
          </Button>
        </div>);

    }

    if (userRole === 'super_admin') {
      return (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleApprove}
            disabled={isDraft || isApproved}
            className={`px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all ${
              isDraft || isApproved
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            <CheckCircle className="w-5 h-5" />
            Approve
          </Button>
          <Button
            onClick={handleReject}
            disabled={isDraft || isRejected}
            className={`px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all ${
              isDraft || isRejected
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            <XCircle className="w-5 h-5" />
            Reject
          </Button>
        </div>);

    }

    if (userRole === 'comms_user') {
      return (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleSendMessage}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all">

            <Send className="w-5 h-5" />
            Send Communication
          </Button>
        </div>);

    }

    return null;
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${isUserMode ? '' : 'flex'}`}>
      {!isUserMode && <AdminSidebarNavigation />}
      <div className={`${isUserMode ? '' : 'flex-1 lg:ml-64'}`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <Button
              onClick={() => navigate(isUserMode ? '/registration-dashboard' : '/admin-dashboard')}
              className="mb-4 text-gray-600 hover:text-gray-900 flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all">

              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </Button>

            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {registrationData?.schoolName}
                    </h1>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(registrationData?.status)}`}>
                      {getStatusLabel(registrationData?.status)}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <span className="font-medium">ID:</span> {registrationData?.id}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="flex items-center gap-1">
                      <span className="font-medium">Category:</span> {registrationData?.category}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="flex items-center gap-1">
                      <span className="font-medium">City:</span> {registrationData?.city}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col items-start lg:items-end gap-2">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Completion:</span>
                    <span className="ml-2 text-lg font-bold text-blue-600">
                      {registrationData?.completionPercentage}%
                    </span>
                  </div>
                  <div className="w-full lg:w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all"
                      style={{ width: `${registrationData?.completionPercentage}%` }} />

                  </div>
                </div>
              </div>

              {!isUserMode && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  {renderQuickActions()}
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md mb-6">
            <div className="border-b border-gray-200 overflow-x-auto">
              <div className="flex min-w-max">
                {tabs?.map((tab) => {
                  const Icon = tab?.icon;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-colors relative ${
                      activeTab === tab?.id ?
                      'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`
                      }>

                      <Icon className="w-5 h-5" />
                      {tab?.label}
                    </button>);

                })}
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'overview' && <TeamOverviewTab data={registrationData} />}
              {activeTab === 'players' && <PlayerDetailsTab players={registrationData?.players} />}
              {activeTab === 'documents' &&
              <DocumentsTab
                documents={registrationData?.documents}
                userRole={isUserMode ? null : userRole} />

              }
              {activeTab === 'declaration' && !isUserMode && (
                <SignedDeclarationTab
                  declarationFilePath={registrationData?.declarationFilePath}
                  registrationId={registrationData?.id}
                />
              )}
              {activeTab === 'activity' && <ActivityHistoryTab activities={registrationData?.activities} />}
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      {showApprovalModal &&
      <ApprovalModal
        registrationId={registrationData?.id}
        schoolName={registrationData?.schoolName}
        onClose={() => setShowApprovalModal(false)}
        onSuccess={handleApprovalSuccess} />

      }
      {showRejectionModal &&
      <RejectionModal
        registrationId={registrationData?.id}
        schoolName={registrationData?.schoolName}
        currentStatus={registrationData?.status}
        onClose={() => setShowRejectionModal(false)}
        onSuccess={handleRejectionSuccess} />

      }
      {showMessageModal &&
      <MessageModal
        recipientName={registrationData?.pocName}
        recipientEmail={registrationData?.pocEmail}
        recipientPhone={registrationData?.pocPhone}
        onClose={() => setShowMessageModal(false)} />

      }
    </div>);

};

export default RegistrationDetailView;